import LastMinuteDealsBadge from '@/app/common/components/Promotions/LastMinuteDealsBadge';
import { PeriodicPromotion } from '@/app/common/components/Promotions/LastMinuteDealsBadge/PeriodicPromotion';
import { getActivePeriodicPromotion } from '@/app/common/periodicPromotions';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider';
import useUserConfig from '@/app/components/hotel-detail/hooks/useUserConfig';
import { useOffersContext } from '@/app/components/hotel-detail/OffersProvider';
import styled, { css } from 'styled-components';
import { getTotalPerNightsLabel } from '@/app/components/search/components/Card/utils/getPriceDetails';
import { useTranslation } from '@/app/components/translations/Translation';
import { Text } from '@lmn/design-system';
import LoyaltyBadge from '@/app/common/components/LoyaltyBadge';
import { DebugRoomInfo } from '@/app/components/hotel-detail/DebugRoomInfo.tsx';
import { useUrlSearchParams } from '../UrlSearchParamsProvider';
import { LocalTaxes } from './LocalTaxes';
import MealPlanPromotion from './MealPlanPromotion';
import { EmployeeDiscountBadge, MobileRatesBadge, MyPriceBadge } from './RoomBadges';
import { MyPriceDiscountPercentage, RoomPrice } from './RoomPrice';

const TotalPricePerNight = styled.div`
  display: flex;
  justify-content: flex-end;
  padding-right: 4px;
`;

type RoomPanelPriceProps = {
  mealPlan: MealPlanType | undefined;
  roomsIncludedInPrice: number;
  roomId: string;
  showPriceDifference?: boolean;
  hidePromotion?: boolean;
  hideTaxes?: boolean;
  hideLmnDeal?: boolean;
  hideLoyaltyBadge?: boolean;
  ref?: React.Ref<HTMLDivElement>;
} & React.HTMLAttributes<HTMLElement>;

export function RoomPanelPrice(props: RoomPanelPriceProps) {
  const {
    mealPlan,
    roomsIncludedInPrice,
    roomId,
    showPriceDifference = false,
    hidePromotion = false,
    hideTaxes = false,
    hideLmnDeal = false,
    hideLoyaltyBadge = false,
    ref,
    ...rest
  } = props;
  const { accommodationOnly, totalGuestsWithoutInfants } = useUrlSearchParams();
  const { t } = useTranslation();
  const { offersData } = useOffersContext();
  const { isDoublePrice, isDoublePriceDP } = useUserConfig();
  const { commonData, localization } = usePageWrapperData();
  const flashSalePromo = mealPlan?.promotions?.find((p) => p.type?.toUpperCase() === 'FLASH_SALE');
  const showLastMinuteDeals = localization?.configValuesApp?.lastMinuteDeals;

  // A configured promotion period (Summer Sale, Black Friday, …) replaces the LMN deal / flash-sale badge
  // with that promotion's badge — mirror the EVO room card so only the periodic promotion shows in these cases.
  const activePeriodicPromotion = getActivePeriodicPromotion(commonData.serverTime, localization);
  const showPeriodicPromotion = !!flashSalePromo && !!activePeriodicPromotion;
  const isLmnDeal = flashSalePromo && showLastMinuteDeals && !activePeriodicPromotion;
  const isLoyalty = mealPlan?.ratePriceType === 'LOYALTY';

  const nights = offersData?.nights;

  return mealPlan ? (
    <PriceContainer {...rest}>
      <div ref={ref} className="room-panel-price-anchor" />
      {(mealPlan.mobileRate && commonData.isMobileDevice) ||
      mealPlan.isMyPrice ||
      flashSalePromo ||
      mealPlan.isEmployeeDiscount ||
      (isLoyalty && !hideLoyaltyBadge) ? (
        <div
          css={css`
            display: flex;
            align-items: flex-end;
            justify-content: flex-end;
            flex-wrap: wrap;
            gap: 4px;
            margin-bottom: 4px;
            &:empty {
              display: none;
            }
          `}
        >
          {isLoyalty && mealPlan.discount && !hideLoyaltyBadge ? <LoyaltyBadge label={mealPlan.discount} /> : undefined}
          {mealPlan.mobileRate && commonData.isMobileDevice ? <MobileRatesBadge /> : undefined}
          {mealPlan.isEmployeeDiscount && <EmployeeDiscountBadge />}
          {isLmnDeal && !hideLmnDeal ? <LastMinuteDealsBadge /> : undefined}
          {showPeriodicPromotion && <PeriodicPromotion />}
          {flashSalePromo ? (
            <MealPlanPromotion
              key={`${flashSalePromo.type}-${flashSalePromo.label}`}
              promotion={flashSalePromo}
              hidePromotion={hidePromotion || isLmnDeal || showPeriodicPromotion}
            />
          ) : undefined}
          {mealPlan.isMyPrice && (
            <>
              <MyPriceBadge />
              <MyPriceDiscountPercentage discountPercentage={mealPlan.discountPercentage} />
            </>
          )}
        </div>
      ) : undefined}
      <div
        css={`
          display: flex;
          justify-content: flex-end;
          gap: 4px;
        `}
      >
        <RoomPrice
          hasFlashSalePromo={!!flashSalePromo}
          mealPlan={mealPlan}
          roomsIncludedInPrice={roomsIncludedInPrice}
          showPriceDifference={showPriceDifference}
        />
      </div>
      {accommodationOnly && (
        <TotalPricePerNight>
          <Text variant="Caption" weight="bold">
            {getTotalPerNightsLabel({ nights, t })}
          </Text>
        </TotalPricePerNight>
      )}
      <LocalTaxes
        css={`
          white-space: nowrap;
          &:empty {
            display: none;
          }

          ${
            isDoublePrice || isDoublePriceDP
              ? `
            flex-direction: column;
            `
              : undefined
          }
        `}
        nights={nights}
        passengers={totalGuestsWithoutInfants}
        roomsIncludedInPrice={roomsIncludedInPrice}
        excludedCharges={hideTaxes ? undefined : mealPlan.excludedCharges}
        excludedChargesIncluded={mealPlan.excludedChargesIncluded}
        price={mealPlan.finalTotalPrice}
        priceWithoutTaxes={mealPlan.priceWithoutTaxes}
        showDoublePrice={(isDoublePrice || isDoublePriceDP) && (accommodationOnly || !showPriceDifference)}
        showTotalPriceIncludedTaxes={true}
      />
      <DebugRoomInfo mealPlan={mealPlan} roomId={roomId} />
    </PriceContainer>
  ) : undefined;
}

const PriceContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  flex-wrap: wrap;
`;
